import React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LoadingOverlay } from "../../components/Loading";
import { ActiveUsersReportQuery } from "../../__generated__/graphql";
import { activeUsersReport, activeUsersSummary, activeUsersReportByRange, activeUsersSummaryByRange } from "./graphql";

const metricPalette = ["#1e88e5", "#43a047", "#fb8c00", "#8e24aa"];

type ChartRow = Record<string, string | number>;
type ChartType = "line" | "area" | "bar";

interface ActiveUsersSummaryQuery {
  analyticsReport?: {
    metricHeaders: string[];
    rows: Array<{ metricValues: string[] }>;
  } | null;
}

const formatMetricLabel = (value: string) =>
  value
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase());

const normalizeDimensionValue = (value: string) => {
  if (!value) return "";
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
};

const parseMetricValue = (value?: string | null) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const compareSortValues = (a: number | string, b: number | string) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

interface ReportsProps {
  startDate?: string;
  endDate?: string;
}

export const Reports = ({ startDate, endDate }: ReportsProps = {}) => {
  const hasRange = !!startDate && !!endDate;
  const rangeVars = hasRange ? { variables: { startDate, endDate } } : undefined;
  const { data, loading, error } = useQuery<ActiveUsersReportQuery>(
    hasRange ? activeUsersReportByRange : activeUsersReport,
    rangeVars
  );
  const { data: summaryData } = useQuery<ActiveUsersSummaryQuery>(
    hasRange ? activeUsersSummaryByRange : activeUsersSummary,
    rangeVars
  );
  const report = data?.analyticsReport;
  const summaryReport = summaryData?.analyticsReport ?? null;
  const dimensionKey = report?.dimensionHeaders?.[0] ?? "dimension";
  const [chartType, setChartType] = React.useState<ChartType>("line");

  const chartData = React.useMemo<ChartRow[]>(() => {
    if (!report) return [];
    const enrichedRows = report.rows.map((row) => {
      const rawDimensionValue = row.dimensionValues[0] ?? "";
      const normalizedDimensionValue = normalizeDimensionValue(rawDimensionValue);
      const sortValue = /^\d{8}$/.test(rawDimensionValue)
        ? Number(rawDimensionValue)
        : normalizedDimensionValue;
      const metrics = report.metricHeaders.reduce<Record<string, number>>(
        (acc, header, index) => {
          acc[header] = parseMetricValue(row.metricValues[index]);
          return acc;
        },
        {}
      );

      return { normalizedDimensionValue, sortValue, metrics };
    });

    return enrichedRows
      .sort((a, b) => compareSortValues(a.sortValue, b.sortValue))
      .map(({ normalizedDimensionValue, metrics }) => ({
        [dimensionKey]: normalizedDimensionValue,
        ...metrics,
      }));
  }, [report, dimensionKey]);

  const summaryTotals = React.useMemo<Record<string, number>>(() => {
    if (!summaryReport || summaryReport.rows.length === 0) return {};
    const summaryRow = summaryReport.rows[0];
    return summaryReport.metricHeaders.reduce<Record<string, number>>(
      (acc, header, index) => {
        acc[header] = parseMetricValue(summaryRow.metricValues[index]);
        return acc;
      },
      {}
    );
  }, [summaryReport]);

  const metricStats = React.useMemo(() => {
    if (!report) return [];
    const totalsRow = report.totals?.[0];

    return report.metricHeaders.map((header, index) => {
      const values = report.rows.map((row) =>
        parseMetricValue(row.metricValues[index])
      );
      const fallbackTotal = values.reduce((acc, value) => acc + value, 0);
      const rawTotal = totalsRow?.metricValues?.[index];
      const summaryTotal = summaryTotals[header];
      const total =
        summaryTotal !== undefined
          ? summaryTotal
          : rawTotal === undefined || rawTotal === null || rawTotal === ""
            ? fallbackTotal
            : parseMetricValue(rawTotal);
      const average = values.length > 0 ? fallbackTotal / values.length : 0;
      const max = values.length > 0 ? Math.max(...values) : 0;
      return {
        key: header,
        label: formatMetricLabel(header),
        total,
        average,
        max,
      };
    });
  }, [report, summaryTotals]);

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: ChartType | null
  ) => {
    if (value) {
      setChartType(value);
    }
  };

  const chartMetrics = report?.metricHeaders ?? [];
  const lastEntries = chartData.slice(-7);

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="error">
          Failed to load analytics: {error.message}
        </Typography>
      </Paper>
    );
  }

  return (
    <LoadingOverlay loading={loading}>
      {!report ? (
        <Paper sx={{ p: 2 }}>
          <Typography>No report data available.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", lg: "repeat(12, 1fr)" },
            }}
          >
            <Paper sx={{ p: 2, gridColumn: { xs: "1 / -1", lg: "span 8" } }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6">Active Users Report</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rows: {report.rowCount ?? report.rows.length}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={handleChartTypeChange}
                  size="small"
                >
                  <ToggleButton value="line">Line</ToggleButton>
                  <ToggleButton value="area">Area</ToggleButton>
                  <ToggleButton value="bar">Bar</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
                {metricStats.map((item) => (
                  <Chip
                    key={item.key}
                    label={`${item.label}: ${item.total.toLocaleString()}`}
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ width: "100%", height: 340, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === "bar" ? (
                    <BarChart
                      data={chartData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={dimensionKey} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {chartMetrics.map((header, index) => (
                        <Bar
                          key={header}
                          dataKey={header}
                          name={formatMetricLabel(header)}
                          fill={metricPalette[index % metricPalette.length]}
                        />
                      ))}
                    </BarChart>
                  ) : chartType === "area" ? (
                    <AreaChart
                      data={chartData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={dimensionKey} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {chartMetrics.map((header, index) => (
                        <Area
                          key={header}
                          type="monotone"
                          dataKey={header}
                          name={formatMetricLabel(header)}
                          stroke={metricPalette[index % metricPalette.length]}
                          fill={metricPalette[index % metricPalette.length]}
                          fillOpacity={0.2}
                          strokeWidth={2}
                        />
                      ))}
                    </AreaChart>
                  ) : (
                    <LineChart
                      data={chartData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={dimensionKey} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {chartMetrics.map((header, index) => (
                        <Line
                          key={header}
                          type="monotone"
                          dataKey={header}
                          name={formatMetricLabel(header)}
                          stroke={metricPalette[index % metricPalette.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Paper>
            <Paper sx={{ p: 2, gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
              <Typography variant="h6">Summary</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
                {metricStats.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      p: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h5">
                      {item.total.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg/day: {item.average.toFixed(1)} • Peak:{" "}
                      {item.max.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Last 7 entries
              </Typography>
              <Box sx={{ width: "100%", height: 180, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lastEntries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={dimensionKey} hide />
                    <YAxis width={30} />
                    <Tooltip />
                    {chartMetrics.map((header, index) => (
                      <Bar
                        key={header}
                        dataKey={header}
                        fill={metricPalette[index % metricPalette.length]}
                        radius={[6, 6, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Daily Breakdown
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{formatMetricLabel(dimensionKey)}</TableCell>
                    {chartMetrics.map((header) => (
                      <TableCell key={header} align="right">
                        {formatMetricLabel(header)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((row, index) => {
                    const dimensionValue = row[dimensionKey];
                    return (
                      <TableRow key={`${String(dimensionValue)}-${index}`}>
                        <TableCell>
                          {typeof dimensionValue === "string"
                            ? dimensionValue
                            : String(dimensionValue)}
                        </TableCell>
                        {chartMetrics.map((header) => {
                          const metricValue = row[header];
                          return (
                            <TableCell key={header} align="right">
                              {typeof metricValue === "number"
                                ? metricValue.toLocaleString()
                                : metricValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </LoadingOverlay>
  );
};
